/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ContentProps, Popup, type TriggerProps } from '@atlaskit/popup';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';

import { MenuControlContext } from '../context';
import { useUniversalCreateAnalytics } from '../hooks';
import type { CreateContentProps, UniversalCreateButtonProps } from '../types';
import { findSelectedItem, useNormalizeItems } from '../utils';

import { MenuContent, MenuWrapper, PopupWrapper, TriggerButton } from './components';
import { useIsResponsiveView } from './utils';

export function UniversalCreateButton({
	children,
	items,
	testId,
	shouldRenderToParent,
	onClick,
	onClose,
	selectedItem,
	product,
	subProduct,
	isOpen: controlledOpen = false,
	enableTriggerPulse = false,
	triggerButtonSpotlightTarget,
	isNav4Enabled = true,
	buttonTheme,
	preventClose = false,
	suggestionConfig = { enabled: false },
	onMouseEnter,
	onMouseLeave,
}: UniversalCreateButtonProps) {
	const isResponsive = useIsResponsiveView();
	const [isOpen, setIsOpen] = useState(controlledOpen || false);
	const structuredItems = useNormalizeItems(items);
	const defaultMenu = useCallback(() => <MenuContent />, []);
	const [createContent, setCreateContent] = useState<(contentProps: ContentProps) => ReactNode>(
		() => defaultMenu,
	);
	const { fireMenuStateChanged } = useUniversalCreateAnalytics();

	const reset = useCallback(() => {
		setCreateContent(() => defaultMenu);
	}, [defaultMenu]);

	const close = useCallback(() => {
		setCreateContent(() => defaultMenu);
		setIsOpen(false);
	}, [defaultMenu]);

	const load = useCallback(
		(loadCreateContent: CreateContentProps) => {
			setCreateContent(
				() => (contentProps: ContentProps) =>
					loadCreateContent(contentProps, {
						reset,
						close,
					}),
			);
		},
		[close, reset],
	);

	const triggerButtonClick = useCallback(() => {
		setIsOpen((prev) => {
			if (prev) {
				onClose?.();
			}
			return !prev;
		});

		onClick?.();
	}, [onClick, onClose, setIsOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<TriggerButton
				testId={testId}
				triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
				enableTriggerPulse={enableTriggerPulse}
				isOpen={isOpen}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onClick={triggerButtonClick}
				triggerProps={triggerProps}
				isNav4Enabled={isNav4Enabled}
				buttonTheme={buttonTheme}
			>
				{children}
			</TriggerButton>
		),
		[
			children,
			enableTriggerPulse,
			isOpen,
			testId,
			triggerButtonClick,
			triggerButtonSpotlightTarget,
			buttonTheme,
			isNav4Enabled,
			onMouseEnter,
			onMouseLeave,
		],
	);

	const onCloseMenu = useCallback(() => {
		if (product === 'confluence' && preventClose) {
			return;
		}

		setIsOpen(false);
		setCreateContent(() => defaultMenu);
		onClose?.();
	}, [defaultMenu, onClose, product, preventClose]);

	const foundItem = useMemo(
		() => selectedItem && findSelectedItem(structuredItems, selectedItem),
		[selectedItem, structuredItems],
	);

	useEffect(() => {
		if (isOpen) {
			const loadContent = foundItem?.loadCreateContent;
			if (loadContent) {
				setCreateContent(
					() => (contentProps: ContentProps) =>
						loadContent(contentProps, {
							reset,
							close,
						}),
				);
			} else {
				reset();
			}
		}
	}, [close, isOpen, foundItem?.loadCreateContent, reset]);

	useEffect(() => {
		if (controlledOpen !== undefined) {
			setIsOpen(controlledOpen);
		}
	}, [controlledOpen]);

	useEffect(() => {
		if (fg('navx-uc-unified-instrumentation')) {
			fireMenuStateChanged({ itemState: isOpen ? 'expanded' : 'collapsed' });
		}
	}, [isOpen, fireMenuStateChanged]);

	const loadPopupContent = useCallback(
		(props: ContentProps) => <MenuWrapper>{createContent(props)}</MenuWrapper>,
		[createContent],
	);

	const AnalyticsWrapper =
		FeatureGates.initializeCalled() &&
		FeatureGates.getExperimentValue(
			'platform_fix_create_button_mutation_element',
			'isEnabled',
			false,
		)
			? AnalyticsWrapperComponent
			: ({ children }: { children: ReactNode }) => {
					if (fg('navx-1690-analytics-context-wrapper')) {
						return (
							<NavigationAnalyticsContext data={{ source: 'topNav' }}>
								{children}
							</NavigationAnalyticsContext>
						);
					}
					return children;
				};

	return (
		<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
			<AnalyticsWrapper>
				<MenuControlContext.Provider
					value={{
						reset,
						close,
						load,
						mixedItems: structuredItems,
						product,
						subProduct,
						suggestionConfig,
						isNav4Enabled,
					}}
				>
					<PopupWrapper>
						{/* eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent */}
						<Popup
							isOpen={isOpen}
							placement={isResponsive && isNav4Enabled ? 'bottom-end' : 'bottom-start'}
							shouldRenderToParent={shouldRenderToParent}
							onClose={onCloseMenu}
							content={loadPopupContent}
							trigger={popupTrigger}
						/>
					</PopupWrapper>
				</MenuControlContext.Provider>
			</AnalyticsWrapper>
		</BFFProvider>
	);
}
const AnalyticsWrapperComponent = ({ children }: { children: ReactNode }) => {
	if (fg('navx-1690-analytics-context-wrapper')) {
		return (
			<NavigationAnalyticsContext data={{ source: 'topNav' }}>
				{children}
			</NavigationAnalyticsContext>
		);
	}
	return children;
};

export default UniversalCreateButton;
